/**
 * Diff view — compares the current buffer against a chosen `plotter:history`
 * row. One of the two features SPEC.md §13 originally listed as a v1
 * stretch goal ("Diff view between two versions of a diagram"), built
 * deliberately scoped: it only ever compares *against history*, so it needs
 * no change to App.tsx's single-buffer model (bufferId, the shelve
 * invariant, URL-hash sharing) — those all keep meaning exactly what they
 * meant before this existed.
 *
 * A backdrop-modal, same shape as ShortcutCheatsheet.tsx: opens from a
 * topbar button (not a `useMenu` dropdown, since picking a row and viewing
 * its diff both happen inside the same overlay), shares `useEscapeKey` for
 * the close behavior.
 *
 * The comparison render is one-shot, not wired through the app's render
 * loop (render.ts) — there's no debounce/sequence-guard need here, the
 * picked row's code never changes while this is open.
 */
import { useEffect, useState } from 'react';
import { type DiffLine, diffLines, hasChanges } from '../lib/diff';
import { readHistory, type HistoryRow } from '../lib/history';
import { renderMermaid } from '../lib/mermaidRender';
import { useEscapeKey } from './useEscapeKey';

interface DiffViewProps {
  currentSource: string;
  currentSvg: string | null;
  onClose: () => void;
}

type ComparisonSide = { status: 'loading' } | { status: 'ok'; svg: string } | { status: 'error'; message: string };

function DiffLineRow({ line }: { line: DiffLine }) {
  const prefix = line.kind === 'add' ? '+' : line.kind === 'remove' ? '−' : ' ';
  const className =
    line.kind === 'add'
      ? 'bg-green/10 text-green'
      : line.kind === 'remove'
        ? 'bg-pink/10 text-pink'
        : 'text-mute';
  return (
    <div className={`whitespace-pre-wrap px-2 font-mono text-[11px] leading-relaxed ${className}`}>
      {prefix} {line.text}
    </div>
  );
}

export default function DiffView({ currentSource, currentSvg, onClose }: DiffViewProps) {
  useEscapeKey(onClose, true);
  // Read once on mount, same reasoning as RecentMenu.tsx's own `isOpen`-gated
  // read: this component only exists while the modal is open, so there's no
  // "closed but still reading localStorage on every App.tsx render" case to
  // guard against here the way RecentMenu does.
  const [rows] = useState<HistoryRow[]>(() => readHistory());
  const [selectedId, setSelectedId] = useState<string | null>(rows[0]?.id ?? null);
  const [comparison, setComparison] = useState<ComparisonSide>({ status: 'loading' });

  const selectedRow = rows.find((row) => row.id === selectedId) ?? null;

  useEffect(() => {
    if (selectedRow === null) return;
    let cancelled = false;
    setComparison({ status: 'loading' });
    void renderMermaid('plotter-diff-preview', selectedRow.code, () => {})
      .then((result) => {
        if (!cancelled) setComparison({ status: 'ok', svg: result.svg });
      })
      .catch((err: unknown) => {
        if (!cancelled) setComparison({ status: 'error', message: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRow]);

  const lines = selectedRow !== null ? diffLines(selectedRow.code, currentSource) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Compare with a recent diagram"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[85vh] w-[90vw] max-w-3xl flex-col rounded-sm border border-ink-3 bg-ink-2 p-4 shadow-lg"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="pane-label shrink-0 text-mute">Compare with</h2>
          {rows.length === 0 ? (
            <p className="text-xs text-mute">No recent diagrams to compare against yet.</p>
          ) : (
            <select
              aria-label="Diagram to compare against"
              value={selectedId ?? ''}
              onChange={(e) => setSelectedId(e.target.value)}
              className="grow rounded-sm border border-ink-3 bg-ink px-2 py-1 text-xs text-text-ink"
            >
              {rows.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.title}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-sm px-2 py-1 text-xs text-mute hover:bg-ink-3 hover:text-text-ink"
          >
            Esc
          </button>
        </div>

        {selectedRow !== null && (
          <div className="flex min-h-0 grow flex-col gap-3 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="pane-label mb-1 text-mute">{selectedRow.title} (before)</p>
                <div className="desk-grid flex h-48 items-center justify-center overflow-hidden rounded-sm">
                  {comparison.status === 'loading' && <p className="text-xs text-text-paper/60">Rendering…</p>}
                  {comparison.status === 'error' && (
                    <p className="px-3 text-center text-[11px] text-pink">{comparison.message}</p>
                  )}
                  {comparison.status === 'ok' && (
                    <div
                      className="max-h-full max-w-full rounded-[3px] bg-sheet p-2 [&_svg]:max-h-[168px] [&_svg]:max-w-full"
                      dangerouslySetInnerHTML={{ __html: comparison.svg }}
                    />
                  )}
                </div>
              </div>
              <div>
                <p className="pane-label mb-1 text-mute">Current (after)</p>
                <div className="desk-grid flex h-48 items-center justify-center overflow-hidden rounded-sm">
                  {currentSvg === null ? (
                    <p className="text-xs text-text-paper/60">Nothing to draw yet.</p>
                  ) : (
                    <div
                      className="max-h-full max-w-full rounded-[3px] bg-sheet p-2 [&_svg]:max-h-[168px] [&_svg]:max-w-full"
                      dangerouslySetInnerHTML={{ __html: currentSvg }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div>
              <p className="pane-label mb-1 text-mute">Source diff</p>
              {!hasChanges(lines) ? (
                <p className="px-2 text-xs text-mute">No source differences.</p>
              ) : (
                <div className="max-h-64 overflow-y-auto rounded-sm border border-ink-3 bg-ink py-1">
                  {lines.map((line, i) => (
                    // Index-keyed: `lines` is a fresh, fully-recomputed array
                    // per `selectedRow` (never reordered/patched in place),
                    // the same tradeoff diagramTypes.ts's own template lists
                    // make for static, wholesale-replaced arrays.
                    <DiffLineRow key={i} line={line} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
